<script lang="ts">
    import { api } from '../lib/api';
    import { toast } from '../lib/stores/toast.svelte';
    import { envs } from '../lib/stores/environment.svelte';
    import type { Consumer } from '../lib/types';
    import { formatRelative } from '../lib/format';

    let consumers = $state<Consumer[]>([]);
    let loading = $state(true);
    let creating = $state(false);
    let newName = $state('');
    let newDesc = $state('');
    let showToken = $state<{ name: string; token: string } | null>(null);

    let movingApp = $state<Consumer | null>(null);
    let moveTargetEnvId = $state('');
    let moving = $state(false);

    // t_de11df77: self-identity management. Deliberately a dedicated dialog
    // flow — NOT a checkbox in the create form (flipping the anchor changes
    // which environment THIS vault instance hydrates at next boot).
    let selfTarget = $state<{ consumer: Consumer; isSelf: boolean } | null>(null);
    let selfBusy = $state(false);

    async function load() {
        if (!envs.currentId) return;
        loading = true;
        try { consumers = await api<Consumer[]>(`/api/consumers?environmentId=${envs.currentId}`); }
        finally { loading = false; }
    }

    $effect(() => {
        if (envs.currentId) load();
    });

    async function create() {
        try {
            const res = await api<{ token: string; name: string }>('/api/consumers', {
                method: 'POST',
                body: JSON.stringify({
                    name: newName,
                    description: newDesc,
                    environmentId: envs.currentId
                }),
            });
            showToken = { name: res.name, token: res.token };
            newName = ''; newDesc = ''; creating = false;
            load();
        } catch (err: any) { toast.error(err.message); }
    }

    async function regenerate(c: Consumer) {
        if (!confirm(`Regenerate token for "${c.name}"? The old token will stop working immediately.`)) return;
        try {
            const res = await api<{ token: string; name: string }>(`/api/consumers/${c.id}/regenerate-token`, { method: 'POST' });
            showToken = { name: res.name, token: res.token };
            load();
        } catch (err: any) { toast.error(err.message); }
    }

    async function del(c: Consumer) {
        if (!confirm(`Delete app "${c.name}"? All its secrets and subscriptions will be removed.`)) return;
        try {
            await api(`/api/consumers/${c.id}`, { method: 'DELETE' });
            toast.success('Deleted');
            load();
        } catch (err: any) { toast.error(err.message); }
    }

    async function saveMove() {
        if (!movingApp || !moveTargetEnvId) return;
        moving = true;
        try {
            await api(`/api/consumers/${movingApp.id}/move`, {
                method: 'POST',
                body: JSON.stringify({ targetEnvironmentId: moveTargetEnvId }),
            });
            toast.success(`App successfully moved!`);
            movingApp = null;
            moveTargetEnvId = '';
            load();
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            moving = false;
        }
    }

    /** PUT the self flag; server warnings (transfer / degraded-clear) surface as toasts. */
    async function confirmSelf() {
        if (!selfTarget) return;
        selfBusy = true;
        try {
            const res = await api<{ consumer: Consumer; warnings: string[] }>(
                `/api/environments/${selfTarget.consumer.environmentId}/consumers/${selfTarget.consumer.id}/self`,
                { method: 'PUT', body: JSON.stringify({ isSelf: selfTarget.isSelf }) }
            );
            for (const w of res.warnings ?? []) toast.push('info', w, 8000); // warnings dwell longer than a default toast
            toast.success(
                selfTarget.isSelf
                    ? `"${selfTarget.consumer.name}" is now this vault's identity.`
                    : `Self-identity cleared from "${selfTarget.consumer.name}".`
            );
            selfTarget = null;
            load();
        } catch (err: any) {
            toast.error(err.message);
        } finally {
            selfBusy = false;
        }
    }

    function copy(s: string) {
        navigator.clipboard.writeText(s);
        toast.success('Copied to clipboard');
    }
</script>

<div class="page-header">
    <h1>Apps</h1>
    <button class="primary" onclick={() => (creating = true)}>+ New App</button>
</div>

{#if loading}
    <p class="muted">Loading...</p>
{:else if !envs.currentId}
    <p class="muted">Please select an environment from the sidebar.</p>
{:else}
    <div class="card">
        <table>
            <thead>
            <tr><th>Name</th><th>Token Prefix</th><th>Last Seen</th><th>Created</th><th></th></tr>
            </thead>
            <tbody>
            {#each consumers as c}
                <tr>
                    <td>
                        <strong>{c.name}</strong>
                        {#if c.isSelf}<span class="badge self-badge" title="This app row is THIS vault instance's own identity (is_self)">Self</span>{/if}
                        {#if c.description}<div class="muted">{c.description}</div>{/if}
                    </td>
                    <td class="mono">{c.tokenPrefix}…</td>
                    <td>{formatRelative(c.lastSeenAt)}</td>
                    <td class="muted">{formatRelative(c.createdAt)}</td>
                    <td class="actions">
                        {#if c.isSelf}
                            <button class="ghost" onclick={() => (selfTarget = { consumer: c, isSelf: false })}>Clear Self Identity…</button>
                        {:else}
                            <button class="ghost" onclick={() => (selfTarget = { consumer: c, isSelf: true })}>Mark as this vault's identity…</button>
                        {/if}
                        <button class="ghost" onclick={() => regenerate(c)}>Regenerate Token</button>
                        <button class="ghost" onclick={() => { movingApp = c; moveTargetEnvId = ''; }}>Move</button>
                        <button class="ghost" onclick={() => del(c)}>🗑</button>
                    </td>
                </tr>
            {:else}
                <tr><td colspan="5" class="muted" style="text-align:center;padding:20px;">No apps currently active in this environment.</td></tr>
            {/each}
            </tbody>
        </table>
    </div>
{/if}

{#if creating}
    <dialog open>
        <header><h2>New App</h2></header>
        <div class="body">
            <label>Name<input bind:value={newName} placeholder="sports-scoreboard" /></label>
            <label>Description<input bind:value={newDesc} placeholder="(optional)" /></label>
        </div>
        <footer>
            <button onclick={() => (creating = false)}>Cancel</button>
            <button class="primary" onclick={create} disabled={!newName}>Create</button>
        </footer>
    </dialog>
{/if}

{#if showToken}
    <dialog open>
        <header><h2>🔑 Token for {showToken.name}</h2></header>
        <div class="body">
            <p class="warning-box">
                <strong>⚠️ This is the only time you'll see this token.</strong>
                Copy it now and paste it into your app's Coolify environment variables.
                If you lose it, you'll need to regenerate.
            </p>
            <div class="token-display">
                <code class="mono">{showToken.token}</code>
                <button class="primary" onclick={() => copy(showToken!.token)}>Copy</button>
            </div>
        </div>
        <footer>
            <button class="primary" onclick={() => (showToken = null)}>I've saved it</button>
        </footer>
    </dialog>
{/if}

{#if movingApp}
    <dialog open>
        <header><h2>Move App: {movingApp.name}</h2></header>
        <div class="body">
            <p class="warning-box" style="margin-bottom: 12px; font-size: 13.5px;">
                When you move an app, <strong>all of its tied secrets and configuration groups are carried with it</strong> to the target environment. <br><br>
                Its API token will not reset, but because its environment location has changed, <strong>it will stop seeing global secrets from the current environment</strong> and start receiving global secrets from the new destination immediately.
            </p>
            <label>
                Destination Environment
                <select bind:value={moveTargetEnvId}>
                    <option value="" disabled selected>Select an environment...</option>
                    {#each envs.list.filter(e => e.id !== envs.currentId) as e}
                        <option value={e.id}>{e.name}</option>
                    {/each}
                </select>
            </label>
        </div>
        <footer>
            <button onclick={() => (movingApp = null)}>Cancel</button>
            <button class="primary" onclick={saveMove} disabled={!moveTargetEnvId || moving}>
                {moving ? 'Moving...' : 'Confirm Move'}
            </button>
        </footer>
    </dialog>
{/if}

{#if selfTarget}
    <dialog open>
        <header>
            <h2>{selfTarget.isSelf ? `Mark "${selfTarget.consumer.name}" as this vault's identity?` : `Clear self-identity from "${selfTarget.consumer.name}"?`}</h2>
        </header>
        <div class="body">
            {#if selfTarget.isSelf}
                <p class="warning-box">
                    <strong>This tells THIS vault instance which environment it serves.</strong>
                    At the next boot the vault hydrates JWT_SECRET, SERVICE_SECRET and MOTHER_API_URL from
                    this environment's Global scope. Only <strong>one app row per database</strong> may carry
                    this flag — setting it here removes it from any other row.<br><br>
                    While this row carries the flag, <strong>deleting this app or its environment is blocked</strong>
                    (clear the flag first — it cannot be destroyed by accident).<br><br>
                    This is a pointer, not a template: the flag does <strong>not</strong> propagate to other
                    environments, and other environments never need a keyvault app row.
                </p>
                <p class="muted hint">Required once per vault instance — without it the vault boots in degraded identity mode.</p>
            {:else}
                <p class="warning-box">
                    <strong>This vault instance will have NO self-identity.</strong>
                    The next boot runs in degraded mode (no_self_consumer): the vault stays up, but the
                    audit outbox relay to Mother will not start until a new identity is set.<br><br>
                    Clearing the flag also <strong>unblocks deleting</strong> this app row and its environment.
                </p>
            {/if}
        </div>
        <footer>
            <button onclick={() => (selfTarget = null)}>Cancel</button>
            <button class="primary" onclick={confirmSelf} disabled={selfBusy}>
                {selfBusy ? 'Working...' : selfTarget.isSelf ? 'Set as this vault\'s identity' : 'Clear self-identity'}
            </button>
        </footer>
    </dialog>
{/if}

<style>
    .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--border); font-size: 13px; }
    th { font-size: 11px; text-transform: uppercase; color: var(--text-2); }
    .actions { text-align: right; }
    .warning-box { background: rgba(245,158,11,0.1); border: 1px solid var(--warning); padding: 12px; border-radius: var(--radius); font-size: 13px; }
    .token-display { display: flex; gap: 8px; align-items: center; background: var(--bg-0); padding: 12px; border-radius: var(--radius); margin-top: 12px; }
    .token-display code { flex: 1; word-break: break-all; font-size: 12px; }
    header { padding: 16px 20px; border-bottom: 1px solid var(--border); }
    header h2 { margin: 0; font-size: 16px; }
    .body { padding: 16px 20px; display: flex; flex-direction: column; gap: 12px; }
    footer { padding: 12px 20px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 8px; }
    label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
    select { padding: 8px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--bg-0); color: var(--text-0); font-size: 13px; }
    .self-badge { margin-left: 8px; padding: 1px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; background: rgba(59,130,246,0.15); border: 1px solid rgba(59,130,246,0.5); color: var(--text-0); }
    .hint { font-size: 12px; }
</style>
